import { supabase } from './supabase';
import { extractFunctionErrorMessage } from '../utils/supabaseFunctionError';

const GENERIC_ERROR = 'No se pudo completar la operación. Intenta nuevamente.';

// Lectura: se apoya enteramente en las políticas RLS ya existentes
// (aplicacion_encabezado/aplicacion_detalle: "auth_user_id = auth.uid()"),
// así que no hace falta ninguna Edge Function para listar ni ver detalle.

export async function listarAplicaciones() {
  const { data, error } = await supabase
    .from('aplicacion_encabezado')
    .select(
      'id, fechaaplicacion, huerto, cuartel, ha, estado, created_at, aplicacion_detalle(id)'
    )
    .order('fechaaplicacion', { ascending: false })
    .order('id', { ascending: false });

  if (error) {
    return { ok: false, message: GENERIC_ERROR };
  }

  const aplicaciones = (data ?? []).map((row) => ({
    id: row.id,
    fechaaplicacion: row.fechaaplicacion,
    huerto: row.huerto,
    cuartel: row.cuartel,
    ha: row.ha,
    estado: row.estado,
    created_at: row.created_at,
    cantidadProductos: Array.isArray(row.aplicacion_detalle) ? row.aplicacion_detalle.length : 0,
  }));

  return { ok: true, aplicaciones };
}

export async function obtenerAplicacion(id) {
  const { data, error } = await supabase
    .from('aplicacion_encabezado')
    .select(
      `id, fechaaplicacion, horainicio, horafin, huerto, cuartel, ha, estado, created_at,
       fecha_anulacion, motivo_anulacion,
       productores ( nombre ),
       aplicacion_detalle ( id, nombrecomercial, dosis )`
    )
    .eq('id', Number(id))
    .maybeSingle();

  if (error) {
    return { ok: false, message: GENERIC_ERROR };
  }
  if (!data) {
    return { ok: false, message: 'La aplicación no existe.' };
  }

  return { ok: true, aplicacion: data };
}

// Escritura: siempre vía Edge Function -> RPC transaccional. Nunca INSERT/
// UPDATE directo desde el cliente (no hay policy de escritura para
// authenticated en estas tablas).
//
// payload puede incluir client_operation_id (uuid) para reintentos
// idempotentes: si ya se creó con ese id, el backend devuelve la misma
// aplicación en vez de duplicarla (ver crear_aplicacion_campo).
//
// networkError:true distingue "no hubo respuesta del servidor" (sin
// conexión real) de un error de validación/negocio con respuesta HTTP —
// el wizard offline usa esta distinción para decidir si debe encolar
// localmente el intento.
export async function crearAplicacion(payload) {
  const { data, error } = await supabase.functions.invoke('crear-aplicacion', { body: payload });
  if (error) {
    const message = await extractFunctionErrorMessage(error);
    const networkError = error?.name === 'FunctionsFetchError';
    return { ok: false, message: message || GENERIC_ERROR, networkError };
  }
  if (!data?.id) {
    return { ok: false, message: GENERIC_ERROR };
  }
  return { ok: true, id: data.id };
}

// Editar/anular siguen siendo online-only en esta fase. `navigator.onLine`
// (usado por los checks previos en las páginas) no es 100% confiable en
// todos los navegadores/escenarios — puede reportar `true` sin conexión
// real — así que además de ese chequeo previo, `networkError:true` aquí
// permite a la página mostrar el mismo mensaje amigable de forma reactiva
// si el fetch en sí falla por red, sin depender solo de navigator.onLine.
export async function actualizarAplicacion(id, payload) {
  const { data, error } = await supabase.functions.invoke('actualizar-aplicacion', {
    body: { ...payload, idaplicacion: Number(id) },
  });
  if (error) {
    const message = await extractFunctionErrorMessage(error);
    const networkError = error?.name === 'FunctionsFetchError';
    return { ok: false, message: message || GENERIC_ERROR, networkError };
  }
  if (!data?.id) {
    return { ok: false, message: GENERIC_ERROR };
  }
  return { ok: true, id: data.id };
}

export async function anularAplicacion(id, motivo) {
  const { data, error } = await supabase.functions.invoke('anular-aplicacion', {
    body: { idaplicacion: Number(id), motivo: motivo || null },
  });
  if (error) {
    const message = await extractFunctionErrorMessage(error);
    const networkError = error?.name === 'FunctionsFetchError';
    return { ok: false, message: message || GENERIC_ERROR, networkError };
  }
  if (!data?.id) {
    return { ok: false, message: GENERIC_ERROR };
  }
  return { ok: true, id: data.id };
}
